import axios from "axios";
import vehicleHelpers from "@/mixins/vehicleHelpers";

export default {
  mixins: [vehicleHelpers],
  props: {
    perPage: {
      type: Number,
      default: 20
    },
    currentPage: {
      type: Number,
      default: 1
    },
    forcedLoading: {
      type: Boolean,
      default: false
    },
    responsive: {
      type: Boolean,
      default: false
    },
    maxResponsiveColumns: {
      type: Number,
      default: 4
    },
    horizontalScrollable: {
      type: Boolean,
      default: false
    },
    disableInfiniteScroll: {
      type: Boolean,
      default: false
    },
    hideHorizontalScrollableButtons: {
      type: Boolean,
      default: false
    },
    showInitialLoadingPlaceholders: {
      type: Boolean,
      default: true
    },
    hideMatchScore: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    },
    scoring: {
      type: Array,
      default: () => []
    },
    sorting: {
      type: Array,
      default: () => [{ _score: "desc" }]
    },
    options: {
      type: Object,
      default: () => ({})
    },
    attachPriceHistory: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      listings: [],
      page: this.currentPage,
      loading: false,
      loaded: false,
      disableSeeMore: true,
      hideSeeMore: false,
      totalPossibleScore: 0,
      innerFilters: null,
      innerScoring: null,
      innerSorting: null,
      innerOptions: null,
      vehicleTotalsData: null
    };
  },
  created() {},
  mounted() {},
  unmounted() {},
  methods: {
    getVehicles() {
      return new Promise((resolve, reject) => {
        this.disableSeeMore = true;
        this.loading = true;

        let thisRequestBody = {
          filters: this.innerFilters ?? this.filters,
          scoring: this.innerScoring ?? this.scoring,
          sorting: [...(this.innerSorting ?? this.sorting)],
          options: this.innerOptions ?? this.options,
          pagination: {
            page: this.page,
            per_page: this.perPage
          }
        };

        // Confirm we have our "required" default options set
        if (!("deduplicate_fields" in thisRequestBody.options)) {
          thisRequestBody.options.deduplicate_fields = ["vin"];
        }
        if (!("exclude_fields" in thisRequestBody.options)) {
          thisRequestBody.options.exclude_fields = [
            "advertiser_account_performance_rollup",
            "inventory_source_performance_rollup",
            "product_performance_rollup"
          ];
        }

        // Autofill the scoring w/ the visitor profile scoring, if we have it
        if (window.localStorage.getItem("carscience-visitor-profile-scoring")) {
          thisRequestBody.options.include_score_field = true;
          thisRequestBody.scoring = [
            ...thisRequestBody.scoring,
            ...JSON.parse(
              window.localStorage.getItem("carscience-visitor-profile-scoring")
            )
          ];
          thisRequestBody.sorting.push({ _score: "desc" });
        }

        // Anytime scoring clauses are included, set our total possible score
        this.totalPossibleScore = 0;
        if (thisRequestBody.scoring.length > 0) {
          let encounteredFieldsHighestScores = {};
          thisRequestBody.scoring.forEach(scoringClauses => {
            let score = +Object.keys(scoringClauses)[0];
            let scoreField = Object.keys(
              scoringClauses[Object.keys(scoringClauses)[0]]
            )[0];
            if (scoreField === "product_id") return;
            if (
              !Object.keys(encounteredFieldsHighestScores).includes(scoreField)
            ) {
              encounteredFieldsHighestScores[scoreField] = score;
            }
            if (score > encounteredFieldsHighestScores[scoreField]) {
              encounteredFieldsHighestScores[scoreField] = score;
            }
          });
          Object.values(encounteredFieldsHighestScores).forEach(fieldScore => {
            this.totalPossibleScore += fieldScore;
          });
        }

        // Fetch the vehicles
        axios
          .post(
            "https://api-v2.jumpfeed.com/network/publisher/products/vehicles",
            thisRequestBody
          )
          .then(response => {
            let facets =
              "facets" in response.data.metadata
                ? response.data.metadata.facets
                : {};
            let vehicles = response.data.data.results;

            // Store totals data directly on component
            this.vehicleTotalsData = response.data.data.totals;

            this.$emit("loaded-facets", facets);
            this.$emit("loaded-vehicles", vehicles);
            this.$emit("loaded-totals", response.data.data.totals);

            if (vehicles.length > 0) {
              vehicles.forEach((vehicle, i) => {
                // beautify.mode=ai.auto
                // vehicles[i].images.unshift("https://image-api.photoroom.com/v2/edit?background.color=transparent&background.scaling=fill&outputSize=2016x1512&padding=15%25&horizontalAlignment=right&shadow.mode=ai.soft&imageUrl=" + vehicle.images[0] + "&apiKey=sandbox_sk_pr_default_e85b26a4bdfa6489a4e72b75a912475e38332f0b")

                vehicles[i]["estimated_monthly_payment"] = this.formatNumber(
                  this.getEstimatedMonthlyVehiclePayment(vehicle.price)
                );
                if (
                  Math.abs(
                    (new Date(vehicle.time_created) - new Date()) / 36e5
                  ) < 168
                ) {
                  vehicles[i]["tags"].unshift({
                    tag: "New Listing",
                    metadata: {},
                    time_created: ""
                  });
                }

                /*if(vehicle.mileage / (((new Date().getFullYear()) - vehicle.year) + 0.5) <= 8000) {
                  vehicles[i]['tags'].push({
                    tag: "Low Mileage",
                    metadata: {},
                    time_created: ""
                  });
                }
                if(vehicle.mileage / (((new Date().getFullYear()) - vehicle.year) + 0.5) >= 20000) {
                  vehicles[i]['tags'].push({
                    tag: "High Mileage",
                    metadata: {},
                    time_created: ""
                  });
                }*/

                vehicles[i].show_match_score = false;
                vehicles[i].match_score = 0;
                vehicles[i].full_match_score = 0;
                if (
                  thisRequestBody.scoring.length > 0 &&
                  this.totalPossibleScore > 0 &&
                  "score" in vehicle
                ) {
                  let matchScore = Math.ceil(
                    (vehicle.score / this.totalPossibleScore) * 100
                  );
                  vehicles[i].full_match_score = matchScore;
                  if (matchScore < 10) matchScore = 10;
                  if (matchScore > 100) matchScore = 100;
                  vehicles[i].show_match_score = true;
                  vehicles[i].match_score = matchScore;
                }
              });

              this.disableSeeMore = false;
              this.page++;
            }

            if (vehicles.length < this.perPage) {
              this.hideSeeMore = true;
            }

            if (this.attachPriceHistory) {
              axios
                .get(
                  "https://api-v2.jumpfeed.com/network/publisher/products/vehicles/price-history?product_id=" +
                    vehicles.map(listing => listing.product_id).join(",")
                )
                .then(response => {
                  if (response.data && response.data.data) {
                    let priceHistory = response.data.data;
                    vehicles.forEach((vehicle, i) => {
                      if (priceHistory[vehicle.product_id]) {
                        vehicles[
                          i
                        ].price_history = this.getVehiclePriceHistoryDetails(
                          priceHistory[vehicle.product_id],
                          vehicle.price,
                          null
                        );
                      }
                    });
                  }
                })
                .finally(() => {
                  this.listings = this.listings.concat(vehicles);
                  this.loading = false;
                  this.loaded = true;
                });
            } else {
              this.listings = this.listings.concat(vehicles);
              this.loading = false;
              this.loaded = true;
            }

            resolve(true);
          })
          .catch(error => {
            console.log(error);
            reject(false);
          });
      });
    }
  }
};
